<script setup>
/* global axios */
// Kanban nativo (migração do AstraChat) — board completo: CRUD, conversas, realtime,
// toolbar (busca/inbox/data/atualizar/opções), ações em massa, card por contato, calendário.
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { createConsumer } from '@rails/actioncable';
import { useMapGetter } from 'dashboard/composables/store';
import { useKanbanStageBadge } from 'dashboard/composables/useKanbanStageBadge';
import draggable from 'vuedraggable';
import KanbanWebhooks from './KanbanWebhooks.vue';

const { t } = useI18n();
const { invalidate: invalidateStageBadges } = useKanbanStageBadge();
const route = useRoute();
const router = useRouter();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;
const currentUser = useMapGetter('getCurrentUser');
let cable = null;
let sub = null;
let darkObserver = null;

const funnels = ref([]);
const activeFunnelId = ref(null);
const board = ref([]); // etapas exibidas (com cards filtrados); draggable muta aqui
const loading = ref(true);
const isDark = ref(false);
const lastUpdate = ref(null);
const viewMode = ref('kanban');

// filtros / toolbar
const search = ref('');
const searchOpen = ref(false);
const inboxFilter = ref(null);
const inboxes = ref([]);
const inboxOpen = ref(false);
const datePreset = ref('all');
const dateOpen = ref(false);
const optionsOpen = ref(false);
const showWebhooks = ref(false);
const addMenuOpen = ref(false);
const funnelOpen = ref(false);

// criação
const showNewStage = ref(false);
const newStageName = ref('');
const addingCardStageId = ref(null);
const newCardName = ref('');
const editingCardId = ref(null);
const editCardName = ref('');

// picker de conversa
const convPickerStage = ref(null);
const convQuery = ref('');
const convResults = ref([]);
const convLoading = ref(false);
// modal de contato
const contactModalStage = ref(null);
const contactQuery = ref('');
const contactResults = ref([]);
const contactTab = ref('search');
const ncName = ref('');
const ncPhone = ref('');
const ncEmail = ref('');

// bulk
const bulkMode = ref(false);
const selected = ref(new Set());
const bulkMoveStage = ref(null);

// gerenciador de funis
const COLORS = [
  '#3B82F6',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#84CC16',
  '#F97316',
  '#6366F1',
];
const funnelMgrOpen = ref(false);
const mgrNewOpen = ref(false);
const mgrName = ref('');
const mgrColor = ref('#3B82F6');
const mgrPublic = ref(false);
const editFunnelId = ref(null);
const efName = ref('');
const efColor = ref('');
const efPublic = ref(false);
const expandedFunnels = ref(new Set());
const stageAddFunnel = ref(null);
const stageAddName = ref('');

const activeFunnel = computed(
  () => funnels.value.find(f => f.id === activeFunnelId.value) || null
);
const inboxLabel = computed(() =>
  inboxFilter.value
    ? inboxes.value.find(i => i.id === inboxFilter.value)?.name ||
      t('KANBAN.inboxFilter.inbox')
    : t('KANBAN.inboxFilter.all')
);

// ---- cores ----
// No claro mistura a cor com branco (pastel suave); no dark escurece a cor (mistura
// com preto) mantendo saturação — dá colunas ricas e vivas com texto branco legível.
const mix = (hex, factor, toWhite) => {
  const h = (hex || '#6B7280').replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const f = c =>
    toWhite ? Math.round(c + (255 - c) * factor) : Math.round(c * factor);
  return `rgb(${f(r)}, ${f(g)}, ${f(b)})`;
};
const colBg = c => (isDark.value ? mix(c, 0.25, false) : mix(c, 0.85, true));
const colHeader = c =>
  isDark.value ? mix(c, 0.35, false) : mix(c, 0.75, true);

// ---- helpers card ----
const cardTitle = c =>
  c.conversation?.contact_name ||
  c.custom_name ||
  `#${c.conversation?.display_id || c.id}`;
const cardPhone = c => c.conversation?.contact_phone || c.contact_phone;
const avatarInitial = c => (cardTitle(c) || '?').trim().charAt(0).toUpperCase();
const openCardDetail = c =>
  router.push(`/app/accounts/${accountId}/kanban/cards/${c.id}`);
const fmtMoney = c =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    (c || 0) / 100
  );
const stageValue = s =>
  (s.cards || []).reduce((sum, c) => sum + (c.total_value || 0), 0);
const leadLabel = s =>
  ({
    won: t('KANBAN.card.leadWonLabel'),
    lost: t('KANBAN.card.leadLostLabel'),
  })[s] || t('KANBAN.card.leadOpenLabel');
const leadCls = s =>
  ({
    won: 'text-n-teal-11 bg-n-teal-3 border-n-teal-6',
    lost: 'text-n-ruby-11 bg-n-ruby-3 border-n-ruby-6',
  })[s] || 'text-n-slate-11 bg-n-alpha-1 border-n-weak';
const leadGlyph = s => ({ won: '✓', lost: '✕' })[s] || '?';
const statusLabel = s =>
  ({
    open: t('KANBAN.convStatus.open'),
    pending: t('KANBAN.convStatus.pending'),
    resolved: t('KANBAN.convStatus.resolved'),
    snoozed: t('KANBAN.convStatus.snoozed'),
  })[s] || s;
const timeAgo = ts => {
  if (!ts) return '';
  const d = Math.floor(Date.now() / 1000) - ts;
  if (d < 60) return t('KANBAN.timeAgo.now');
  if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`;
  if (d < 604800) return `${Math.floor(d / 86400)}d`;
  return new Date(ts * 1000).toLocaleDateString('pt-BR');
};

// ---- filtro + montagem do board ----
const matchCard = c => {
  if (inboxFilter.value && c.conversation?.inbox_id !== inboxFilter.value)
    return false;
  if (datePreset.value !== 'all') {
    const now = Math.floor(Date.now() / 1000);
    const presetDays = { today: 1, '7d': 7, '30d': 30 };
    const cut = now - (presetDays[datePreset.value] || 30) * 86400;
    if ((c.conversation?.last_activity_at || c.updated_at || 0) < cut)
      return false;
  }
  const q = search.value.trim().toLowerCase();
  if (q) {
    const hay =
      `${c.custom_name || ''} ${c.conversation?.contact_name || ''} ${cardPhone(c) || ''} ${c.conversation?.display_id || ''} ${c.id}`.toLowerCase();
    if (!hay.includes(q)) return false;
  }
  return true;
};
const rebuildBoard = () => {
  board.value = activeFunnel.value
    ? activeFunnel.value.stages.map(s => ({
        ...s,
        cards: s.cards.filter(matchCard),
      }))
    : [];
};
watch([search, inboxFilter, datePreset, activeFunnelId], rebuildBoard);

const load = async () => {
  const { data } = await axios.get(`${base}/funnels`);
  funnels.value = data.data || [];
  if (!activeFunnel.value) activeFunnelId.value = funnels.value[0]?.id ?? null;
  lastUpdate.value = new Date();
  loading.value = false;
  rebuildBoard();
};
const loadInboxes = async () => {
  try {
    inboxes.value = (await axios.get(`${base}/inboxes`)).data.data || [];
  } catch (e) {
    /* noop */
  }
};

// ---- Funil ----
// gerenciador de funis (tela de criação/edição fiel ao original)
// "Gerenciar Funis" é uma PÁGINA própria (rota), não modal — navega pra ela
const openFunnelMgr = () => {
  funnelOpen.value = false;
  optionsOpen.value = false;
  router.push(`/app/accounts/${accountId}/kanban/funnels`);
};
const openFunnelMgrNew = () => openFunnelMgr();
const createFunnelFull = async () => {
  const name = mgrName.value.trim();
  if (!name) return;
  const { data } = await axios.post(`${base}/funnels`, {
    name,
    color: mgrColor.value,
    is_public: mgrPublic.value,
  });
  mgrNewOpen.value = false;
  mgrName.value = '';
  await load();
  activeFunnelId.value = data.data.id;
};
const startEditFunnel = f => {
  editFunnelId.value = f.id;
  efName.value = f.name;
  efColor.value = f.color;
  efPublic.value = f.is_public;
};
const saveFunnelEdit = async () => {
  await axios.patch(`${base}/funnels/${editFunnelId.value}`, {
    name: efName.value,
    color: efColor.value,
    is_public: efPublic.value,
  });
  editFunnelId.value = null;
  await load();
};
const deleteFunnelById = async f => {
  if (!window.confirm(t('KANBAN.prompts.deleteFunnelCards', { name: f.name })))
    return;
  await axios.delete(`${base}/funnels/${f.id}`);
  if (activeFunnelId.value === f.id) activeFunnelId.value = null;
  await load();
};
const toggleExpand = id => {
  const s = new Set(expandedFunnels.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  expandedFunnels.value = s;
};
const addStageTo = async f => {
  const n = stageAddName.value.trim();
  if (!n) return;
  await axios.post(`${base}/funnels/${f.id}/stages`, { name: n });
  stageAddName.value = '';
  stageAddFunnel.value = null;
  await load();
};
const toggleFunnelActive = async f => {
  await axios.patch(`${base}/funnels/${f.id}`, { is_active: !f.is_active });
  await load();
};
const duplicateFunnel = async f => {
  await axios.post(`${base}/funnels/${f.id}/duplicate`);
  await load();
};
const deleteFunnel = async () => {
  if (
    !activeFunnel.value ||
    !window.confirm(
      t('KANBAN.prompts.deleteFunnelAllCards', {
        name: activeFunnel.value.name,
      })
    )
  )
    return;
  await axios.delete(`${base}/funnels/${activeFunnelId.value}`);
  activeFunnelId.value = null;
  await load();
};
// ---- Etapa ----
const createStage = async () => {
  const name = newStageName.value.trim();
  if (!name || !activeFunnel.value) return;
  await axios.post(`${base}/funnels/${activeFunnelId.value}/stages`, { name });
  newStageName.value = '';
  showNewStage.value = false;
  await load();
};
const renameStage = async s => {
  const n = window.prompt(t('KANBAN.prompts.renameStage'), s.name);
  if (!n) return;
  await axios.patch(`${base}/stages/${s.id}`, { name: n });
  await load();
};
const deleteStage = async s => {
  if (!window.confirm(t('KANBAN.prompts.deleteStage', { name: s.name })))
    return;
  await axios.delete(`${base}/stages/${s.id}`);
  await load();
};
// ---- Card ----
const openAddCard = id => {
  addingCardStageId.value = id;
  newCardName.value = '';
};
const createCard = async id => {
  const n = newCardName.value.trim();
  if (!n) return;
  await axios.post(`${base}/cards`, { stage_id: id, custom_name: n });
  newCardName.value = '';
  addingCardStageId.value = null;
  await load();
};
const startEditCard = c => {
  editingCardId.value = c.id;
  editCardName.value = c.custom_name || '';
};
const saveCard = async c => {
  const n = editCardName.value.trim();
  if (n && n !== c.custom_name)
    await axios.patch(`${base}/cards/${c.id}`, { custom_name: n });
  editingCardId.value = null;
  await load();
};
const setLeadStatus = async (c, st) => {
  await axios.patch(`${base}/cards/${c.id}`, { lead_status: st });
  await load();
};
const cycleLead = c =>
  setLeadStatus(
    c,
    { open: 'won', won: 'lost', lost: 'open' }[c.lead_status || 'open']
  );
const deleteCard = async c => {
  if (!window.confirm(t('KANBAN.prompts.deleteCard'))) return;
  await axios.delete(`${base}/cards/${c.id}`);
  await load();
};
// ---- Conversas ----
const searchConvs = async () => {
  convLoading.value = true;
  convResults.value =
    (
      await axios.get(`${base}/conversations`, {
        params: { q: convQuery.value },
      })
    ).data.data || [];
  convLoading.value = false;
};
const openConvPicker = async id => {
  addMenuOpen.value = false;
  convPickerStage.value = id;
  convQuery.value = '';
  await searchConvs();
};
const addConvCard = async cv => {
  await axios.post(`${base}/cards`, {
    stage_id: convPickerStage.value,
    conversation_id: cv.id,
  });
  convPickerStage.value = null;
  await load();
};
const openConversation = d =>
  router.push(`/app/accounts/${accountId}/conversations/${d}`);
// ---- Contato ----
const firstStageId = () => activeFunnel.value?.stages?.[0]?.id ?? null;
const searchContacts = async () => {
  contactResults.value =
    (await axios.get(`${base}/contacts`, { params: { q: contactQuery.value } }))
      .data.data || [];
};
const openContactModal = () => {
  addMenuOpen.value = false;
  contactModalStage.value = firstStageId();
  contactTab.value = 'search';
  contactQuery.value = '';
  contactResults.value = [];
  ncName.value = '';
  ncPhone.value = '';
  ncEmail.value = '';
  searchContacts();
};
const addContactCard = async ct => {
  await axios.post(`${base}/cards`, {
    stage_id: contactModalStage.value,
    contact_id: ct.id,
  });
  contactModalStage.value = null;
  await load();
};
const createContactCard = async () => {
  if (!ncName.value.trim()) return;
  const { data } = await axios.post(`${base}/contacts`, {
    name: ncName.value,
    phone_number: ncPhone.value,
    email: ncEmail.value,
  });
  await addContactCard(data.data);
};
const openAddAvulso = () => {
  addMenuOpen.value = false;
  openAddCard(firstStageId());
};
// ---- Bulk ----
const toggleBulk = () => {
  bulkMode.value = !bulkMode.value;
  selected.value = new Set();
  optionsOpen.value = false;
};
const toggleSelect = id => {
  const s = new Set(selected.value);
  if (s.has(id)) s.delete(id);
  else s.add(id);
  selected.value = s;
};
const bulkDelete = async () => {
  if (
    !selected.value.size ||
    !window.confirm(
      t('KANBAN.prompts.bulkDeleteCards', { count: selected.value.size })
    )
  )
    return;
  await Promise.all(
    [...selected.value].map(id =>
      axios.delete(`${base}/cards/${id}`).catch(() => {})
    )
  );
  selected.value = new Set();
  await load();
};
const bulkMove = async () => {
  if (!selected.value.size || !bulkMoveStage.value) return;
  await Promise.all(
    [...selected.value].map(id =>
      axios
        .patch(`${base}/cards/${id}/move`, {
          stage_id: bulkMoveStage.value,
          position: 0,
        })
        .catch(() => {})
    )
  );
  selected.value = new Set();
  bulkMoveStage.value = null;
  invalidateStageBadges();
  await load();
};
// ---- Drag ----
const onChange = async (stage, evt) => {
  const ch = evt.added || evt.moved;
  if (!ch) return;
  await axios.patch(`${base}/cards/${ch.element.id}/move`, {
    stage_id: stage.id,
    position: ch.newIndex,
  });
  invalidateStageBadges();
  await load();
};

// ---- Calendário ----
const calCards = computed(() =>
  board.value.flatMap(s =>
    s.cards.map(c => ({ ...c, _stage: s.name, _color: s.color }))
  )
);
const calMonth = ref(new Date());
const calGrid = computed(() => {
  const y = calMonth.value.getFullYear();
  const m = calMonth.value.getMonth();
  const first = new Date(y, m, 1);
  const start = new Date(first);
  start.setDate(1 - first.getDay());
  const days = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    days.push(d);
  }
  return days.map(d => {
    const ds = d.toDateString();
    const cards = calCards.value.filter(c => {
      const ts = c.conversation?.last_activity_at || c.updated_at;
      return ts && new Date(ts * 1000).toDateString() === ds;
    });
    return { date: d, inMonth: d.getMonth() === m, cards };
  });
});
const calTitle = computed(() =>
  calMonth.value.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
);
const calShift = n => {
  const d = new Date(calMonth.value);
  d.setMonth(d.getMonth() + n);
  calMonth.value = d;
};

// ---- Realtime / dark ----
const subscribeRealtime = () => {
  const u = currentUser.value;
  if (!u?.pubsub_token) return;
  cable = createConsumer();
  sub = cable.subscriptions.create(
    {
      channel: 'KanbanChannel',
      pubsub_token: u.pubsub_token,
      account_id: accountId,
      user_id: u.id,
    },
    {
      received: () => {
        if (
          editingCardId.value ||
          convPickerStage.value ||
          contactModalStage.value ||
          addingCardStageId.value !== null
        )
          return;
        load();
      },
    }
  );
};
const syncDark = () => {
  // o Chatwoot aplica a classe `dark` no <body> (themeHelper.js), NÃO no <html>
  isDark.value = document.body.classList.contains('dark');
};
onMounted(() => {
  syncDark();
  darkObserver = new MutationObserver(syncDark);
  darkObserver.observe(document.body, {
    attributes: true,
    attributeFilter: ['class'],
  });
  load();
  loadInboxes();
  subscribeRealtime();
});
onUnmounted(() => {
  try {
    sub?.unsubscribe();
    cable?.disconnect();
    darkObserver?.disconnect();
  } catch (e) {
    /* noop */
  }
});
</script>

<template>
  <div class="flex flex-col w-full h-full overflow-hidden">
    <!-- Toolbar -->
    <div
      class="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-n-weak bg-n-background dark:bg-n-solid-1"
    >
      <!-- logo -->
      <svg
        class="w-6 h-6 text-n-brand shrink-0"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
      >
        <rect x="4" y="4" width="4" height="16" rx="1" stroke-width="2" />
        <rect x="10" y="4" width="4" height="10" rx="1" stroke-width="2" />
        <rect x="16" y="4" width="4" height="13" rx="1" stroke-width="2" />
      </svg>
      <!-- view toggle -->
      <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-1 dark:bg-n-solid-3">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-n-slate-10"
          :class="{
            'bg-n-background text-n-slate-12 shadow-sm': viewMode === 'kanban',
          }"
          @click="viewMode = 'kanban'"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2"
            /></svg
          >{{ t('KANBAN.viewMode.kanban') }}
        </button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md text-n-slate-10"
          :class="{
            'bg-n-background text-n-slate-12 shadow-sm':
              viewMode === 'calendar',
          }"
          @click="viewMode = 'calendar'"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            /></svg
          >{{ t('KANBAN.viewMode.calendar') }}
        </button>
      </div>
      <!-- funil -->
      <div v-if="funnels.length" class="relative">
        <button
          class="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="
            funnelOpen = !funnelOpen;
            inboxOpen = false;
            dateOpen = false;
          "
        >
          <span
            class="w-2 h-2 rounded-full shrink-0"
            :style="{ backgroundColor: activeFunnel?.color || '#3B82F6' }"
          />
          <span class="max-w-[150px] truncate">{{
            activeFunnel?.name || t('KANBAN.funnel')
          }}</span>
          <svg
            class="w-3.5 h-3.5 text-n-slate-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        <div
          v-if="funnelOpen"
          class="absolute top-[110%] left-0 z-50 min-w-[180px] rounded-lg border border-n-weak bg-n-background shadow-xl p-1 max-h-80 overflow-y-auto"
        >
          <button
            v-for="f in funnels"
            :key="f.id"
            class="flex items-center gap-2 w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
            @click="
              activeFunnelId = f.id;
              funnelOpen = false;
            "
          >
            <span
              class="w-2 h-2 rounded-full shrink-0"
              :style="{ backgroundColor: f.color || '#3B82F6' }"
            />{{ f.name }}
          </button>
          <div class="my-1 border-t border-n-weak" />
          <button
            class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-blue-11 hover:bg-n-alpha-1"
            @click="openFunnelMgrNew()"
          >
            {{ t('KANBAN.funnelMenu.newFunnel') }}
          </button>
          <button
            class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
            @click="openFunnelMgr()"
          >
            {{ t('KANBAN.funnelMenu.manageFunnels') }}
          </button>
        </div>
      </div>
      <!-- inbox -->
      <div class="relative">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="
            inboxOpen = !inboxOpen;
            dateOpen = false;
          "
        >
          <svg
            class="w-3.5 h-3.5 shrink-0"
            viewBox="0 0 24 24"
            fill="currentColor"
          >
            <path
              d="M12 2c1 3-1 4-2 6-1 2 0 4 2 4s2-2 1-4c3 2 4 5 4 7a7 7 0 1 1-14 0c0-3 2-6 5-9 1 3 3 3 4 0z"
            />
          </svg>
          <span class="max-w-[130px] truncate">{{ inboxLabel }}</span>
          <svg
            class="w-3.5 h-3.5 text-n-slate-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        <div
          v-if="inboxOpen"
          class="absolute top-[110%] left-0 z-50 min-w-[180px] rounded-lg border border-n-weak bg-n-background shadow-xl p-1 max-h-80 overflow-y-auto"
        >
          <button
            class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
            @click="
              inboxFilter = null;
              inboxOpen = false;
            "
          >
            {{ t('KANBAN.inboxFilter.all') }}
          </button>
          <button
            v-for="i in inboxes"
            :key="i.id"
            class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
            @click="
              inboxFilter = i.id;
              inboxOpen = false;
            "
          >
            {{ i.name }}
          </button>
        </div>
      </div>
      <!-- data -->
      <div class="relative">
        <button
          class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
          :class="{ 'text-n-brand': datePreset !== 'all' }"
          :title="t('KANBAN.dateFilter.title')"
          @click="
            dateOpen = !dateOpen;
            inboxOpen = false;
          "
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </button>
        <div
          v-if="dateOpen"
          class="absolute top-[110%] left-0 z-50 min-w-[180px] rounded-lg border border-n-weak bg-n-background shadow-xl p-1 max-h-80 overflow-y-auto"
        >
          <button
            v-for="pp in ['all', 'today', '7d', '30d']"
            :key="pp"
            class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
            @click="
              datePreset = pp;
              dateOpen = false;
            "
          >
            {{
              {
                all: t('KANBAN.dateFilter.all'),
                today: t('KANBAN.dateFilter.today'),
                '7d': t('KANBAN.dateFilter.last7d'),
                '30d': t('KANBAN.dateFilter.last30d'),
              }[pp]
            }}
          </button>
        </div>
      </div>
      <!-- busca -->
      <div class="flex items-center">
        <div
          v-if="searchOpen"
          class="flex items-center gap-1 px-2 py-1 bg-n-background border rounded-lg border-n-weak dark:bg-n-solid-2"
        >
          <svg
            class="w-4 h-4 text-n-slate-10"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
            />
          </svg>
          <input
            v-model="search"
            :placeholder="t('KANBAN.search.placeholder')"
            class="text-sm bg-transparent outline-none w-52 text-n-slate-12"
            @keyup.esc="
              search = '';
              searchOpen = false;
            "
          />
          <button
            v-if="search"
            class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
            @click="search = ''"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <button
          v-else
          class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
          :title="t('KANBAN.search.button')"
          @click="searchOpen = true"
        >
          <svg
            class="w-4 h-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
            />
          </svg>
        </button>
      </div>

      <div class="flex items-center gap-1 ml-auto">
        <span v-if="lastUpdate" class="mr-1 text-xs text-n-slate-10">{{
          t('KANBAN.lastUpdate', { time: lastUpdate.toLocaleTimeString() })
        }}</span>
        <!-- add -->
        <div v-if="activeFunnel" class="relative">
          <button
            class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
            :title="t('KANBAN.addMenu.add')"
            @click="addMenuOpen = !addMenuOpen"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
          </button>
          <div
            v-if="addMenuOpen"
            class="absolute top-[110%] right-0 left-auto z-50 min-w-[180px] rounded-lg border border-n-weak bg-n-background shadow-xl p-1 max-h-80 overflow-y-auto"
          >
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="openAddAvulso"
            >
              {{ t('KANBAN.addMenu.standaloneCard') }}
            </button>
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="openContactModal"
            >
              {{ t('KANBAN.addMenu.contactCard') }}
            </button>
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="openConvPicker(firstStageId())"
            >
              {{ t('KANBAN.addMenu.fromConversation') }}
            </button>
          </div>
        </div>
        <!-- opções (engrenagem) -->
        <div class="relative">
          <button
            class="inline-flex items-center justify-center w-8 h-8 rounded-md text-n-slate-11 hover:bg-n-alpha-1"
            :title="t('KANBAN.header.moreOptions')"
            @click="optionsOpen = !optionsOpen"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <circle cx="12" cy="12" r="3" stroke-width="2" />
            </svg>
          </button>
          <div
            v-if="optionsOpen"
            class="absolute top-[110%] right-0 left-auto z-50 min-w-[180px] rounded-lg border border-n-weak bg-n-background shadow-xl p-1 max-h-80 overflow-y-auto"
          >
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="
                load();
                optionsOpen = false;
              "
            >
              {{ t('KANBAN.optionsMenu.refresh') }}
            </button>
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="openFunnelMgr()"
            >
              {{ t('KANBAN.funnelMenu.manageFunnels') }}
            </button>
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="
                showWebhooks = true;
                optionsOpen = false;
              "
            >
              {{ t('KANBAN.webhooks.menuLabel') }}
            </button>
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="toggleBulk"
            >
              {{ bulkMode ? '✓ ' : ''
              }}{{ t('KANBAN.optionsMenu.bulkActions') }}
            </button>
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-slate-12 hover:bg-n-alpha-1"
              @click="
                showNewStage = true;
                optionsOpen = false;
              "
            >
              {{ t('KANBAN.optionsMenu.newColumn') }}
            </button>
            <button
              class="block w-full text-left px-2.5 py-2 text-sm rounded-md text-n-ruby-11 hover:bg-n-alpha-1"
              @click="
                deleteFunnel();
                optionsOpen = false;
              "
            >
              {{ t('KANBAN.optionsMenu.deleteFunnel') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- bulk bar -->
    <div
      v-if="bulkMode"
      class="flex items-center gap-2 px-4 py-2 border-b bg-n-iris-3 dark:bg-n-solid-2 border-n-weak"
    >
      <span class="text-sm text-n-iris-11 dark:text-n-iris-11">{{
        t('KANBAN.bulkBar.selectedCount', { count: selected.size })
      }}</span>
      <select
        v-model="bulkMoveStage"
        class="pl-2 pr-8.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
      >
        <option :value="null">{{ t('KANBAN.bulkBar.moveTo') }}</option>
        <option
          v-for="s in activeFunnel?.stages || []"
          :key="s.id"
          :value="s.id"
        >
          {{ s.name }}
        </option>
      </select>
      <button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
        @click="bulkMove"
      >
        {{ t('KANBAN.bulkBar.move') }}
      </button>
      <button
        class="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-n-ruby-9 text-white hover:brightness-110"
        @click="bulkDelete"
      >
        {{ t('KANBAN.bulkBar.delete') }}
      </button>
      <button
        class="ml-auto inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="toggleBulk"
      >
        {{ t('KANBAN.bulkBar.exitSelection') }}
      </button>
    </div>

    <div v-if="loading" class="p-6 text-n-slate-11">
      {{ t('KANBAN.loading') }}
    </div>
    <div
      v-else-if="!activeFunnel"
      class="flex flex-col items-start gap-2 p-6 text-n-slate-11"
    >
      {{ t('KANBAN.noFunnelYet')
      }}<button
        class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
        @click="openFunnelMgrNew"
      >
        {{ t('KANBAN.createFunnelBtn') }}
      </button>
    </div>

    <!-- BOARD -->
    <div
      v-else-if="viewMode === 'kanban'"
      class="flex flex-1 gap-3 p-4 overflow-x-auto"
    >
      <div
        v-for="stage in board"
        :key="stage.id"
        class="flex flex-col rounded-lg w-[300px] min-w-[280px] shrink-0 max-h-full"
        :style="{ backgroundColor: colBg(stage.color) }"
      >
        <div
          class="flex items-center gap-2 px-3 py-2.5 rounded-t-lg group"
          :style="{ backgroundColor: colHeader(stage.color) }"
        >
          <span
            class="w-3 h-3 rounded-full shrink-0"
            :style="{ backgroundColor: stage.color }"
          />
          <h3
            class="text-heading-3 truncate"
            :class="isDark ? 'text-slate-100' : 'text-n-slate-12'"
          >
            {{ stage.name }}
          </h3>
          <span
            v-if="stageValue(stage) > 0"
            class="ml-auto flex items-center gap-1 px-2 py-1 text-[0.6875rem] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full dark:text-green-300 dark:bg-green-900/30 dark:border-green-800"
          >
            <svg
              class="w-3.5 h-3.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
              />
            </svg>
            {{ fmtMoney(stageValue(stage)) }}
          </span>
          <span
            class="flex items-center justify-center w-7 h-7 text-xs font-medium text-n-slate-10 bg-n-background rounded-full shadow-sm shrink-0 dark:text-n-slate-11 dark:bg-n-solid-2"
            :class="{ 'ml-auto': stageValue(stage) === 0 }"
            >{{ stage.cards.length }}</span
          >
          <span class="flex gap-1 transition opacity-0 group-hover:opacity-100">
            <button
              class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
              :title="t('KANBAN.actions.rename')"
              @click="renameStage(stage)"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"
                />
              </svg>
            </button>
            <button
              class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
              :title="t('KANBAN.actions.delete')"
              @click="deleteStage(stage)"
            >
              <svg
                class="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                />
              </svg>
            </button>
          </span>
        </div>
        <draggable
          :list="stage.cards"
          :group="bulkMode ? 'none' : 'kanban'"
          :disabled="bulkMode"
          item-key="id"
          class="flex-1 p-2 space-y-2 overflow-y-auto min-h-[40px]"
          @change="evt => onChange(stage, evt)"
        >
          <template #item="{ element }">
            <div
              class="p-3 bg-n-background border border-n-weak rounded-lg shadow-sm dark:bg-n-solid-1 dark:border-n-slate-6 hover:shadow-md group/card"
              :class="[
                {
                  'ring-2 ring-purple-400 border-purple-500':
                    bulkMode && selected.has(element.id),
                  'opacity-75': element.lead_status === 'lost',
                },
                bulkMode ? 'cursor-pointer' : 'cursor-grab',
              ]"
              @click="bulkMode && toggleSelect(element.id)"
            >
              <div v-if="editingCardId === element.id" class="flex gap-1">
                <input
                  v-model="editCardName"
                  class="grow px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                  @keyup.enter="saveCard(element)"
                  @keyup.esc="editingCardId = null"
                />
                <button
                  class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
                  @click="saveCard(element)"
                >
                  {{ t('KANBAN.funnelManagement.ok') }}
                </button>
              </div>
              <template v-else>
                <!-- header: nome (link) + ações -->
                <div class="flex items-start justify-between gap-2 mb-2">
                  <div class="flex items-center gap-1 min-w-0">
                    <span
                      v-if="bulkMode"
                      class="flex items-center justify-center w-5 h-5 border-2 rounded shrink-0"
                      :class="
                        selected.has(element.id)
                          ? 'bg-purple-600 border-purple-600 text-white'
                          : 'border-gray-300'
                      "
                      >{{ selected.has(element.id) ? '✓' : '' }}</span
                    >
                    <button
                      class="text-sm font-semibold truncate hover:underline"
                      :class="
                        element.conversation
                          ? 'text-n-blue-11 dark:text-n-blue-11'
                          : 'text-n-slate-12 dark:text-slate-200'
                      "
                      :title="t('KANBAN.card.openDetails')"
                      @click.stop="openCardDetail(element)"
                    >
                      {{ cardTitle(element) }}
                    </button>
                    <span
                      v-if="!element.conversation"
                      class="px-1.5 py-0.5 text-[0.625rem] text-purple-600 bg-purple-50 border border-purple-200 rounded dark:text-purple-300 dark:bg-purple-900/20 shrink-0"
                      >{{ t('KANBAN.card.standaloneAvulso') }}</span
                    >
                    <button
                      v-if="!element.conversation && !bulkMode"
                      class="shrink-0 inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                      :title="t('KANBAN.actions.rename')"
                      @click.stop="startEditCard(element)"
                    >
                      <svg
                        class="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"
                        />
                      </svg>
                    </button>
                  </div>
                  <div class="flex items-center gap-1.5 shrink-0">
                    <span
                      v-if="element.conversation?.unread_count"
                      class="px-1.5 py-0.5 text-[0.625rem] font-medium text-white bg-red-500 rounded-full"
                      >{{ element.conversation.unread_count }}</span
                    >
                    <span
                      class="text-[0.6875rem] text-n-slate-10 dark:text-n-slate-10"
                      >{{
                        timeAgo(
                          element.conversation?.last_activity_at ||
                            element.updated_at
                        )
                      }}</span
                    >
                    <button
                      v-if="element.conversation && !bulkMode"
                      class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                      :title="t('KANBAN.card.openInChatwoot')"
                      @click.stop="
                        openConversation(element.conversation.display_id)
                      "
                    >
                      <svg
                        class="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path d="M7 17 17 7M7 7h10v10" />
                      </svg>
                    </button>
                    <button
                      v-if="!bulkMode"
                      class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                      :title="t('KANBAN.actions.delete')"
                      @click.stop="deleteCard(element)"
                    >
                      <svg
                        class="w-4 h-4"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2"
                      >
                        <path
                          d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <!-- contato: avatar + nome + telefone -->
                <div
                  v-if="element.conversation || cardPhone(element)"
                  class="flex items-center gap-2 mb-2"
                >
                  <div
                    class="flex items-center justify-center text-sm font-medium text-white rounded-full w-8 h-8 shrink-0 bg-blue-500 dark:bg-n-blue-9"
                  >
                    {{ avatarInitial(element) }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div
                      class="text-sm font-medium truncate text-n-slate-12 dark:text-n-slate-12"
                    >
                      {{ cardTitle(element) }}
                    </div>
                    <div
                      v-if="cardPhone(element)"
                      class="text-xs truncate text-n-slate-10 dark:text-n-slate-11"
                    >
                      {{ cardPhone(element) }}
                    </div>
                  </div>
                </div>
                <!-- itens/produtos -->
                <div
                  v-if="element.items && element.items.length"
                  class="mb-2 space-y-0.5"
                >
                  <div
                    v-for="it in element.items.slice(0, 3)"
                    :key="it.id"
                    class="flex items-center justify-between gap-2 text-xs"
                  >
                    <span
                      class="flex-1 truncate text-n-slate-10 dark:text-n-slate-11"
                      >{{ it.title }}</span
                    >
                    <span
                      class="font-medium text-n-teal-11 whitespace-nowrap dark:text-n-teal-11"
                      >{{
                        fmtMoney((it.value || 0) * (it.quantity || 1))
                      }}</span
                    >
                  </div>
                  <div
                    v-if="element.items.length > 3"
                    class="text-xs text-n-slate-10 dark:text-n-slate-11"
                  >
                    {{
                      t('KANBAN.moreItems', { count: element.items.length - 3 })
                    }}
                  </div>
                </div>
                <!-- footer: status do lead (ícone) + pílulas -->
                <div class="flex flex-wrap items-center gap-1.5">
                  <button
                    class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border"
                    :class="leadCls(element.lead_status)"
                    :title="t('KANBAN.card.leadStatusToggle')"
                    @click.stop="cycleLead(element)"
                  >
                    <span
                      class="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full border border-current text-[0.5625rem]"
                      >{{ leadGlyph(element.lead_status) }}</span
                    >{{ leadLabel(element.lead_status) }}
                  </button>
                  <span
                    v-if="element.conversation?.inbox_name"
                    class="px-2 py-0.5 text-[0.6875rem] text-gray-600 bg-gray-100 rounded dark:text-n-slate-11 dark:bg-n-solid-3"
                    >{{ element.conversation.inbox_name }}</span
                  >
                  <span
                    v-if="element.conversation?.assignee_name"
                    class="px-2 py-0.5 text-[0.6875rem] text-purple-700 bg-purple-100 rounded dark:text-n-iris-11 dark:bg-n-iris-3"
                    >{{
                      element.conversation.assignee_name.split(' ')[0]
                    }}</span
                  >
                  <span
                    v-for="lb in (element.conversation?.labels || []).slice(
                      0,
                      2
                    )"
                    :key="lb"
                    class="px-2 py-0.5 text-[0.6875rem] text-blue-700 bg-blue-100 rounded dark:text-n-blue-11 dark:bg-n-blue-3"
                    >{{ lb }}</span
                  >
                  <span
                    v-if="element.total_value > 0"
                    class="px-2 py-0.5 ml-auto text-[0.6875rem] font-semibold text-green-700 bg-green-100 rounded dark:text-green-300 dark:bg-green-900/30"
                    >{{ fmtMoney(element.total_value) }}</span
                  >
                </div>
              </template>
            </div>
          </template>
        </draggable>
        <div class="flex flex-col gap-1 p-2 pt-0">
          <div v-if="addingCardStageId === stage.id" class="flex gap-1">
            <input
              v-model="newCardName"
              :placeholder="t('KANBAN.standaloneCardModal.titlePlaceholder')"
              class="grow px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              @keyup.enter="createCard(stage.id)"
              @keyup.esc="addingCardStageId = null"
            />
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="createCard(stage.id)"
            >
              +
            </button>
          </div>
          <template v-else>
            <button
              class="w-full px-2 py-1.5 text-xs rounded-md border border-dashed border-n-weak text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2"
              @click="openAddCard(stage.id)"
            >
              {{ t('KANBAN.addStandaloneCardBtn') }}
            </button>
            <button
              class="w-full px-2 py-1.5 text-xs rounded-md border border-dashed border-n-weak text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2"
              @click="openConvPicker(stage.id)"
            >
              {{ t('KANBAN.addConversationBtn') }}
            </button>
          </template>
        </div>
      </div>
      <div class="flex flex-col w-64 shrink-0">
        <div
          v-if="showNewStage"
          class="flex flex-col gap-1 p-2 rounded-lg bg-n-alpha-1"
        >
          <input
            v-model="newStageName"
            :placeholder="t('KANBAN.funnelManagement.stageName')"
            class="px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            @keyup.enter="createStage"
            @keyup.esc="showNewStage = false"
          />
          <div class="flex gap-1">
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="createStage"
            >
              {{ t('KANBAN.funnelManagement.create') }}</button
            ><button
              class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
              @click="showNewStage = false"
            >
              {{ t('KANBAN.funnelManagement.cancel') }}
            </button>
          </div>
        </div>
        <button
          v-else
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="showNewStage = true"
        >
          {{ t('KANBAN.addColumnBtn') }}
        </button>
      </div>
    </div>

    <!-- CALENDÁRIO -->
    <div v-else class="flex flex-col flex-1 p-4 overflow-auto">
      <div class="flex items-center gap-3 mb-3">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="calShift(-1)"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <h3 class="text-heading-3 capitalize text-n-slate-12">
          {{ calTitle }}
        </h3>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
          @click="calShift(1)"
        >
          <svg
            class="w-4 h-4"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
      <div
        class="grid grid-cols-7 gap-1 text-xs font-medium text-center text-n-slate-10"
      >
        <span
          v-for="d in ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']"
          :key="d"
          >{{ t('KANBAN.calendar.weekdays.' + d) }}</span
        >
      </div>
      <div class="grid flex-1 grid-cols-7 gap-1 mt-1">
        <div
          v-for="(cell, i) in calGrid"
          :key="i"
          class="p-1 border rounded border-n-weak min-h-[80px]"
          :class="cell.inMonth ? '' : 'opacity-40'"
        >
          <div class="text-[0.6875rem] text-n-slate-10">
            {{ cell.date.getDate() }}
          </div>
          <div
            v-for="c in cell.cards.slice(0, 3)"
            :key="c.id"
            class="mt-0.5 px-1 text-[0.625rem] text-white rounded truncate"
            :style="{ backgroundColor: c._color }"
            :title="cardTitle(c)"
          >
            {{ cardTitle(c) }}
          </div>
          <div
            v-if="cell.cards.length > 3"
            class="text-[0.625rem] text-n-slate-10"
          >
            +{{ cell.cards.length - 3 }}
          </div>
        </div>
      </div>
    </div>

    <!-- Modal picker de conversas -->
    <div
      v-if="convPickerStage"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="convPickerStage = null"
    >
      <div
        class="bg-n-background rounded-xl shadow-xl w-[90%] max-w-[460px] p-4"
      >
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('KANBAN.convPicker.title') }}
          </h3>
          <button
            class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
            @click="convPickerStage = null"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <input
          v-model="convQuery"
          :placeholder="t('KANBAN.convPicker.searchPlaceholder')"
          class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          @keyup.enter="searchConvs"
        />
        <div class="mt-3 overflow-y-auto max-h-80">
          <div v-if="convLoading" class="p-3 text-sm text-n-slate-10">
            {{ t('KANBAN.convPicker.searching') }}
          </div>
          <div
            v-else-if="!convResults.length"
            class="p-3 text-sm text-n-slate-10"
          >
            {{ t('KANBAN.convPicker.noConversations') }}
          </div>
          <button
            v-for="c in convResults"
            :key="c.id"
            class="flex items-center w-full gap-2 p-2 text-left rounded-md hover:bg-n-alpha-1"
            @click="addConvCard(c)"
          >
            <span
              class="flex items-center justify-center text-xs font-medium text-white rounded-full w-7 h-7 shrink-0 bg-n-blue-9"
              >{{ (c.contact_name || '?').charAt(0).toUpperCase() }}</span
            >
            <span class="flex flex-col min-w-0"
              ><span class="text-sm truncate text-n-slate-12">{{
                c.contact_name || t('KANBAN.card.noName')
              }}</span
              ><span class="text-xs text-n-slate-10"
                >#{{ c.display_id }} · {{ statusLabel(c.status) }}</span
              ></span
            >
          </button>
        </div>
      </div>
    </div>

    <!-- Modal contato -->
    <div
      v-if="contactModalStage"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="contactModalStage = null"
    >
      <div
        class="bg-n-background rounded-xl shadow-xl w-[90%] max-w-[460px] p-4"
      >
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-heading-3 text-n-slate-12">
            {{ t('KANBAN.addMenu.contactCard') }}
          </h3>
          <button
            class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
            @click="contactModalStage = null"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="flex gap-1 mb-3">
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md text-n-slate-10"
            :class="{
              'bg-n-background text-n-slate-12 shadow-sm':
                contactTab === 'search',
            }"
            @click="contactTab = 'search'"
          >
            {{ t('KANBAN.contactModal.tabSearch') }}
          </button>
          <button
            class="px-3 py-1.5 text-sm font-medium rounded-md text-n-slate-10"
            :class="{
              'bg-n-background text-n-slate-12 shadow-sm': contactTab === 'new',
            }"
            @click="contactTab = 'new'"
          >
            {{ t('KANBAN.contactModal.tabNew') }}
          </button>
        </div>
        <template v-if="contactTab === 'search'">
          <input
            v-model="contactQuery"
            :placeholder="t('KANBAN.contactModal.searchPlaceholder')"
            class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            @keyup.enter="searchContacts"
          />
          <div class="mt-3 overflow-y-auto max-h-72">
            <div
              v-if="!contactResults.length"
              class="p-3 text-sm text-n-slate-10"
            >
              {{ t('KANBAN.contactModal.noContacts') }}
            </div>
            <button
              v-for="c in contactResults"
              :key="c.id"
              class="flex flex-col w-full gap-0.5 p-2 text-left rounded-md hover:bg-n-alpha-1"
              @click="addContactCard(c)"
            >
              <span class="text-sm text-n-slate-12">{{
                c.name || t('KANBAN.card.noName')
              }}</span>
              <span class="text-xs text-n-slate-10">{{
                c.phone_number || c.email || '—'
              }}</span>
            </button>
          </div>
        </template>
        <template v-else>
          <div class="flex flex-col gap-2">
            <input
              v-model="ncName"
              :placeholder="t('KANBAN.contactModal.namePlaceholder')"
              class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
            <input
              v-model="ncPhone"
              :placeholder="t('KANBAN.contactModal.phonePlaceholder')"
              class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
            <input
              v-model="ncEmail"
              :placeholder="t('KANBAN.contactModal.emailPlaceholder')"
              class="w-full px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
            <button
              class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
              @click="createContactCard"
            >
              {{ t('KANBAN.contactModal.createContactAndCard') }}
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- Modal: gerenciador de funis (criação/edição) -->
    <div
      v-if="funnelMgrOpen"
      class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-n-alpha-black2"
      @click.self="funnelMgrOpen = false"
    >
      <div
        class="bg-n-background rounded-xl shadow-xl w-[90%] max-w-[760px] p-0 max-h-[88vh] flex flex-col"
      >
        <div class="flex items-center gap-2 px-4 py-3.5 border-b border-n-weak">
          <h2 class="text-heading-2 text-n-slate-12">
            {{ t('KANBAN.funnelManagement.manageFunnels') }}
          </h2>
          <button
            class="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
            @click="
              mgrNewOpen = true;
              mgrName = '';
              mgrColor = '#3B82F6';
              mgrPublic = false;
            "
          >
            {{ t('KANBAN.createNewFunnelBtn') }}
          </button>
          <button
            class="ml-1 inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
            @click="funnelMgrOpen = false"
          >
            <svg
              class="w-4 h-4"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div class="flex flex-col gap-3 p-4 overflow-y-auto">
          <!-- form de criação -->
          <div
            v-if="mgrNewOpen"
            class="border-2 border-n-brand/40 bg-n-alpha-1 rounded-lg p-3.5"
          >
            <div class="flex items-center justify-between mb-2">
              <h3 class="text-heading-3 text-n-slate-12">
                {{ t('KANBAN.newFunnelTitle') }}
              </h3>
              <button
                class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                @click="mgrNewOpen = false"
              >
                <svg
                  class="w-4 h-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                >
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <input
              v-model="mgrName"
              :placeholder="t('KANBAN.funnelManagement.funnelNamePlaceholder')"
              class="w-full mb-2 px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
            <div class="flex items-center gap-2 mb-2">
              <span class="text-sm text-n-slate-11">{{
                t('KANBAN.funnelManagement.colorLabel')
              }}</span>
              <button
                v-for="c in COLORS"
                :key="c"
                class="w-[22px] h-[22px] rounded-full"
                :class="{
                  'ring-2 ring-offset-2 ring-n-strong': mgrColor === c,
                }"
                :style="{ backgroundColor: c }"
                @click="mgrColor = c"
              />
            </div>
            <label
              class="flex items-center gap-2 mb-3 text-sm cursor-pointer text-n-slate-11"
              ><input v-model="mgrPublic" type="checkbox" />
              {{ t('KANBAN.funnelManagement.publicAllCanSee') }}</label
            >
            <div class="flex gap-2">
              <button
                class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
                @click="createFunnelFull"
              >
                {{ t('KANBAN.funnelManagement.createFunnel') }}</button
              ><button
                class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
                @click="mgrNewOpen = false"
              >
                {{ t('KANBAN.funnelManagement.cancel') }}
              </button>
            </div>
          </div>
          <!-- lista de funis -->
          <div
            v-for="f in funnels"
            :key="f.id"
            class="border border-n-weak rounded-lg overflow-hidden bg-n-background dark:bg-n-solid-2"
          >
            <div
              class="flex items-center gap-2 px-3 py-2.5 bg-n-solid-2 border-b border-n-weak"
            >
              <template v-if="editFunnelId === f.id">
                <input
                  v-model="efName"
                  class="grow px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                />
                <button
                  v-for="c in COLORS"
                  :key="c"
                  class="w-[22px] h-[22px] rounded-full"
                  :class="{
                    'ring-2 ring-offset-2 ring-n-strong': efColor === c,
                  }"
                  :style="{ backgroundColor: c }"
                  @click="efColor = c"
                />
                <button
                  class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
                  @click="saveFunnelEdit"
                >
                  {{ t('KANBAN.funnelManagement.save') }}
                </button>
                <button
                  class="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
                  @click="editFunnelId = null"
                >
                  {{ t('KANBAN.funnelManagement.cancel') }}
                </button>
              </template>
              <template v-else>
                <button
                  class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                  @click="toggleExpand(f.id)"
                >
                  {{ expandedFunnels.has(f.id) ? '▾' : '▸' }}
                </button>
                <span
                  class="w-4 h-4 rounded-full shrink-0"
                  :style="{ backgroundColor: f.color }"
                />
                <span class="font-semibold text-n-slate-12">{{ f.name }}</span>
                <span class="text-sm text-n-slate-10">{{
                  t('KANBAN.stagesCount', { count: f.stages.length })
                }}</span>
                <span
                  v-if="!f.is_active"
                  class="px-2 py-0.5 text-[0.625rem] font-medium rounded-full bg-n-alpha-2 text-n-slate-11"
                  >{{ t('KANBAN.funnelManagement.inactive') }}</span
                >
                <span
                  v-if="f.is_system"
                  class="px-2 py-0.5 text-[0.625rem] font-medium rounded-full bg-n-iris-3 text-n-iris-11"
                  >{{ t('KANBAN.funnelManagement.systemBadge') }}</span
                >
                <span
                  class="px-2 py-0.5 text-[0.625rem] font-medium rounded-full"
                  :class="
                    f.is_public
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                  >{{
                    f.is_public
                      ? t('KANBAN.funnelManagement.publicBadge')
                      : t('KANBAN.privateBadge')
                  }}</span
                >
                <span class="flex items-center gap-2 ml-auto">
                  <button
                    class="relative w-9 h-5 rounded-full transition-colors"
                    :class="f.is_active ? 'bg-n-teal-9' : 'bg-n-slate-5'"
                    :title="
                      f.is_active
                        ? t('KANBAN.funnelManagement.active')
                        : t('KANBAN.funnelManagement.inactive')
                    "
                    @click="toggleFunnelActive(f)"
                  >
                    <span
                      class="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                      :class="f.is_active ? 'left-[18px]' : 'left-0.5'"
                    />
                  </button>
                  <button
                    class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                    :title="t('KANBAN.actions.edit')"
                    @click="startEditFunnel(f)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"
                      />
                    </svg>
                  </button>
                  <button
                    v-if="!f.is_system"
                    class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                    :title="t('KANBAN.actions.duplicate')"
                    @click="duplicateFunnel(f)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <rect x="9" y="9" width="11" height="11" rx="2" />
                      <path
                        d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"
                      />
                    </svg>
                  </button>
                  <button
                    v-if="!f.is_system"
                    class="inline-flex items-center justify-center px-1.5 text-n-ruby-11 hover:brightness-90"
                    :title="t('KANBAN.actions.delete')"
                    @click="deleteFunnelById(f)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                      />
                    </svg>
                  </button>
                </span>
              </template>
            </div>
            <div
              v-if="expandedFunnels.has(f.id) && editFunnelId !== f.id"
              class="flex flex-col gap-1 pt-2 pr-3 pb-3 pl-9"
            >
              <div
                v-for="s in f.stages"
                :key="s.id"
                class="flex items-center gap-2 py-0.5"
              >
                <span
                  class="w-3 h-3 rounded-full shrink-0"
                  :style="{ backgroundColor: s.color }"
                />
                <span class="text-sm text-n-slate-12">{{ s.name }}</span>
                <span class="flex gap-1 ml-auto"
                  ><button
                    class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                    @click="renameStage(s)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4z"
                      />
                    </svg></button
                  ><button
                    class="inline-flex items-center justify-center px-1.5 text-n-slate-10 hover:text-n-slate-12"
                    @click="deleteStage(s)"
                  >
                    <svg
                      class="w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                    >
                      <path
                        d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"
                      />
                    </svg>
                  </button>
                </span>
              </div>
              <div v-if="stageAddFunnel === f.id" class="flex gap-1 mt-1">
                <input
                  v-model="stageAddName"
                  :placeholder="t('KANBAN.funnelManagement.stageName')"
                  class="grow px-2.5 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
                  @keyup.enter="addStageTo(f)"
                />
                <button
                  class="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-110"
                  @click="addStageTo(f)"
                >
                  +
                </button>
              </div>
              <button
                v-else
                class="mt-1 px-2 py-1.5 text-xs rounded-md border border-dashed border-n-weak text-n-slate-10 bg-n-alpha-1 hover:bg-n-alpha-2"
                @click="
                  stageAddFunnel = f.id;
                  stageAddName = '';
                "
              >
                {{ t('KANBAN.addStageBtn') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <KanbanWebhooks
      v-if="showWebhooks"
      :account-id="accountId"
      @close="showWebhooks = false"
    />
  </div>
</template>
